import {
    AfterViewInit,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {CommonConfig} from '@ui-app/configs/common.config';
import {MediaErrors} from '@ui-app/models/common.enums';
import {IDownloadUrlResponse, IUploadUrlRequest, PhotoData, UPLOAD_URL} from '@ui-app/models/common.models';
import {AppToastrService} from '@ui-app/services/toastr.service';
import {EMPTY, Observable, of, Subject} from 'rxjs';
import {first, map, switchMap, tap} from 'rxjs/operators';
import {BaseComponent} from '../../base.component';
import {PhotoUploadService} from './photo-upload.service';
import {S3DownloadUrlService} from '@ui-app/services/s3-download-url.service';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';

@Component({
    selector: 'ui-camera',
    templateUrl: './camera.component.html',
    styleUrls: ['./camera.component.less'],
})
export class CameraComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    private static defaultVideoOptions: MediaTrackConstraints = { facingMode: 'environment' };

    @ContentChild('takePhoto', { static: true, read: TemplateRef }) takePhotoTpl: TemplateRef<HTMLButtonElement>;
    @ViewChild('takePhoto', { static: true }) takePhotoDefaultTpl: TemplateRef<HTMLButtonElement>;

    @ContentChild('savePhoto', { static: true, read: TemplateRef }) savePhotoTpl: TemplateRef<HTMLButtonElement>;
    @ViewChild('savePhoto', { static: true }) savePhotoDefaultTpl: TemplateRef<HTMLButtonElement>;

    @ViewChild('notSupportedMediaServices', { static: true, read: TemplateRef })
    private notSupportedMediaServices: TemplateRef<HTMLElement>;
    @ViewChild('disabledByBrowser', { static: true, read: TemplateRef }) private disabledByBrowser: TemplateRef<HTMLElement>;
    @ViewChild('connectedCamera', { static: true, read: TemplateRef }) private connectedCamera: TemplateRef<HTMLElement>;
    @ViewChild('userPhoto', { static: true, read: TemplateRef }) private userPhoto: TemplateRef<HTMLElement>;
    @ViewChild('disabledByOS', { static: true, read: TemplateRef }) private disabledByOS: TemplateRef<HTMLElement>;

    @Input() userCanTakePhoto = true;
    @Input() width: number = 640;
    @Input() height: number = 480;
    @Input() autoplay: boolean;
    @Input() defaultConfig = CommonConfig.cameraConfig;
    @Input() imageType: string = this.defaultConfig.defaultImageType;
    @Input() imageQuality: number = this.defaultConfig.defaultImageQuality;
    @Input() newFileName: string;

    photoSrc: string;

    @Input()
    set userPhotoUrl(value: string) {
        if (value === '' && this.userCanTakePhoto) {
            this.initCamera();
        } else if (value && value.length > 0) {
            this.stopMediaTracks();
        }
        this.photoSrc = value;
    }

    @Output() onPhotoTaken: EventEmitter<PhotoData> = new EventEmitter();

    componentInitialized: boolean;
    isDisabledByBrowser: boolean = false;
    mediaStream: MediaStream = null;
    availableVideoInputs: MediaDeviceInfo[] = [];
    selectedDeviceId: string = null;

    dataUrl: string;
    imageData: ImageData;
    uploadInProgress: boolean;

    saveAction$ = new Subject<IUploadUrlRequest>();

    get isMediaServicesSupported(): boolean {
        return navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function';
    }

    get isUserMediaSupported(): boolean {
        return navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';
    }

    get imageObject(): Record<string, string> {
        if (this.dataUrl && this.imageData) {
            return ({
                contentType: this.imageType,
                dataUrl: this.dataUrl
            });
        } else {
            return null;
        }
    }

    private video: HTMLVideoElement;

    @ViewChild('video')
    set videoElement(element: ElementRef) {
        if (element) {
            this.video = element.nativeElement;
        }
    }

    get videoElement(): HTMLVideoElement {
        return this.video;
    }

    private canvas: HTMLCanvasElement;

    @ViewChild('canvas')
    set canvasElement(element: ElementRef) {
        if (element) {
            this.canvas = element.nativeElement;
        }
    }

    get canvasElement(): HTMLCanvasElement {
        return this.canvas;
    }

    isCameraActionsDisabled: boolean;

    constructor(
        private toastrSevice: AppToastrService,
        @Optional() @Inject(UPLOAD_URL) private uploadUrl: string,
        private downloadUrlService: S3DownloadUrlService,
        @Optional() @Inject(PhotoUploadService) private photoUploadService: PhotoUploadService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.handleActions();
    }

    private handleActions(): void {
        /**
         * save photo
         */
        let photoData;
        let downloadUrlResponse;
        this.subs = this.saveAction$
            .pipe(
                switchMap((request) => this.photoUploadService.uploadPhoto(request, this.uploadUrl, this.dataUrl)),
                tap((photo: PhotoData) => photoData = photo),
                switchMap((photo) => this.downloadUrlService.getUrl(photo.uId)),
                tap((urlResponse: IDownloadUrlResponse) => downloadUrlResponse = urlResponse),
                switchMap(() => of({ ...photoData, ...downloadUrlResponse })),
            )
            .subscribe((data: PhotoData) => {
                    this.onPhotoTaken.emit(data);
                    this.uploadInProgress = false;
                    this.dataUrl = null;
                },
                (error) => {
                    this.toastrSevice.error('Something went wrong. Please refresh the page and try again', null);
                    this.uploadInProgress = false;
                });
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.componentInitialized = true;
            if (this.userCanTakePhoto) {
                this.initCamera();
            }
        });
    }

    ngOnDestroy(): void {
        this.stopMediaTracks();
    }

    getTemplate(): TemplateRef<HTMLElement> {

        if (!this.userCanTakePhoto) {
            this.isCameraActionsDisabled = true;
            return this.userPhoto;
        }

        if (!this.isMediaServicesSupported) {
            this.isCameraActionsDisabled = true;
            return this.notSupportedMediaServices;
        }

        if (this.isDisabledByBrowser && !this.photoSrc) {
            this.isCameraActionsDisabled = true;
            return this.disabledByBrowser;
        }

        if (this.isDisabledByBrowser && this.photoSrc) {
            this.isCameraActionsDisabled = true;
            return this.userPhoto;
        }

        if (this.photoSrc) {
            this.isCameraActionsDisabled = false;
            return this.userPhoto;
        }

        if (this.selectedDeviceId || this.dataUrl) {
            this.isCameraActionsDisabled = false;
            return this.connectedCamera;
        }

        this.isCameraActionsDisabled = true;
        return this.disabledByOS;
    }

    initCamera(deviceId: string = null, videoOptions?: MediaTrackConstraints): void {
        this.subs = this.getAvailableVideoInputs()
            .pipe(
                first(Boolean),
                tap((devices: MediaDeviceInfo[]) => {
                    this.availableVideoInputs = devices;
                    this.dataUrl = null;
                    this.imageData = null;

                    if (devices.length > 0) {
                        this.isDisabledByBrowser = devices.every(v => !v.deviceId);
                    }

                    if (this.autoplay) {
                        this.selectedDeviceId = this.availableVideoInputs[0].deviceId;
                    }
                }),
                switchMap(() => {
                    this.selectedDeviceId = deviceId || this.selectedDeviceId;
                    const videoTrackConstraints = this.getMediaConstraintsForDevice(this.selectedDeviceId, videoOptions);
                    return this.getUserMediaStream(videoTrackConstraints);
                }),
                first(Boolean),
            )
            .subscribe((stream: MediaStream) => {
                    if (this.isDisabledByBrowser && this.userCanTakePhoto) {
                        this.initCamera();
                        return;
                    }

                    this.mediaStream = stream;
                    if (this.selectedDeviceId) {
                        const video = this.video;
                        video.srcObject = stream;
                        video.play();
                    } else {
                        this.stopMediaTracks();
                    }
                },
                error => {
                    switch (error.name) {
                        case MediaErrors.NotAllowedError:
                            this.isDisabledByBrowser = true;
                            break;
                        case MediaErrors.EmptyError:
                            break;
                        default:
                            this.availableVideoInputs = [];
                            this.selectedDeviceId = null;
                            break;
                    }
                });
    }

    stopMediaTracks(): void {
        if (this.mediaStream && this.mediaStream.getTracks) {
            this.mediaStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
            this.selectedDeviceId = null;
        }
    }

    private getAvailableVideoInputs(): Observable<MediaDeviceInfo[]> {
        if (!this.isMediaServicesSupported) {
            return EMPTY;
        }

        return fromPromise(navigator.mediaDevices.enumerateDevices())
            .pipe(
                map((devices: MediaDeviceInfo[]) => devices.filter((device: MediaDeviceInfo) => device.kind === 'videoinput')),
            );
    }

    private getUserMediaStream(videoTrackConstraints: MediaTrackConstraints): Observable<MediaStream> {
        if (!this.isUserMediaSupported) {
            return EMPTY;
        }

        return fromPromise(navigator.mediaDevices.getUserMedia({ video: videoTrackConstraints, audio: false }));
    }

    private getMediaConstraintsForDevice(deviceId: string, baseMediaTrackConstraints: MediaTrackConstraints): MediaTrackConstraints {
        const result: MediaTrackConstraints = baseMediaTrackConstraints
            ? baseMediaTrackConstraints
            : CameraComponent.defaultVideoOptions;

        if (deviceId) {
            result.deviceId = { exact: deviceId };
        }

        return result;
    }

    /**
     * CAMERA ACTIONS
     */
    takeSnapshot(): void {
        const video = this.video;
        const canvas = this.canvas;
        canvas.width = video.videoWidth || this.width;
        canvas.height = video.videoHeight || this.height;

        const imageWidth = this.defaultConfig.width || canvas.width;
        const imageHeight = this.defaultConfig.height || canvas.height;

        const context2d = canvas.getContext('2d');
        context2d.drawImage(video, 0, 0);

        // data for upload
        this.dataUrl = canvas.toDataURL('image/jpeg', this.imageQuality);
        this.imageData = context2d.getImageData(0, 0, imageWidth, imageHeight);

        this.dataUrl = this.photoUploadService.recursiveCompress(canvas, this.dataUrl, this.imageQuality);

        // turn off camera
        this.stopMediaTracks();

        this.onPhotoTaken.emit({ dataImage: this.dataUrl, uId: null });
    }

    uploadPhoto(): void {
        if (this.imageObject) {
            this.uploadInProgress = true;

            const request: IUploadUrlRequest = {
                // tslint:disable-next-line:max-line-length
                name: this.newFileName ? `${this.newFileName}.${this.imageType.split('/')[1]}\`` : `${Date.now()}.${this.imageType.split('/')[1]}`,
                contentType: this.imageType,
                length: this.imageData.data.length
            };
            this.saveAction$.next(request);
        }
    }
}
